import { useState } from 'react';
import { routePath, type DistributorWithRoutes, type RouteSummary } from './api';
import { RouteMap } from './RouteMap';

const routeCollator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });
const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

interface RouteCardRow {
  route: RouteSummary;
  distributorId: number;
  distributorName: string;
}

export function RouteIndex({
  distributors,
  selectedDistributorIds,
  onSelectDistributors,
}: {
  distributors: DistributorWithRoutes[];
  selectedDistributorIds: number[] | null;
  onSelectDistributors: (ids: number[]) => void;
}) {
  const [search, setSearch] = useState('');
  const multipleDistributors = distributors.length > 1;
  const availableIds = distributors.map((distributor) => distributor.id);
  const selectedIds =
    selectedDistributorIds === null
      ? availableIds
      : availableIds.filter((id) => selectedDistributorIds.includes(id));
  const selectedSet = new Set(selectedIds);
  const query = search.trim().toLocaleLowerCase();
  const rows: RouteCardRow[] = distributors
    .filter((distributor) => selectedSet.has(distributor.id))
    .flatMap((distributor) =>
      distributor.routes.map((route) => ({
        route,
        distributorId: distributor.id,
        distributorName: distributor.name,
      })),
    )
    .filter(
      (row) =>
        query === '' ||
        [row.route.routeNumber, row.distributorName, ...(row.route.cities ?? [])].some((value) =>
          value.toLocaleLowerCase().includes(query),
        ),
    )
    .sort(
      (a, b) =>
        routeCollator.compare(a.distributorName, b.distributorName) ||
        routeCollator.compare(a.route.routeNumber, b.route.routeNumber),
    );

  return (
    <section className="route-index" aria-labelledby="route-index-title">
      {multipleDistributors && (
        <div className="distributor-selection" aria-label="Filter routes by distributor">
          <div className="selection-heading">
            <strong>Distributors</strong>
            <button
              type="button"
              className="show-all-button"
              onClick={() => onSelectDistributors(availableIds)}
              disabled={selectedIds.length === distributors.length}
            >
              Show all
            </button>
          </div>
          <div className="distributor-buttons">
            {distributors.map((distributor) => {
              const included = selectedSet.has(distributor.id);
              return (
                <button
                  key={distributor.id}
                  type="button"
                  className="distributor-button"
                  aria-pressed={included}
                  onClick={() =>
                    onSelectDistributors(
                      included
                        ? selectedIds.filter((id) => id !== distributor.id)
                        : [...selectedIds, distributor.id],
                    )
                  }
                >
                  <span className="selection-icon" aria-hidden="true">
                    {included ? '✓' : '+'}
                  </span>
                  {distributor.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="index-heading">
        <div>
          <h2 id="route-index-title">Your routes</h2>
          <p>
            {multipleDistributors
              ? `${selectedIds.length} of ${plural(distributors.length, 'distributor')} selected. Choose a route to see its customers.`
              : 'Choose a route to see its customers.'}
          </p>
        </div>
        <span className="count-pill">{plural(rows.length, 'route')}</span>
      </div>

      <div className="route-card-toolbar">
        <label htmlFor="route-search">Find a route</label>
        <input
          id="route-search"
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search route, distributor, or city"
        />
      </div>

      {rows.length ? (
        <div className="route-cards">
          {rows.map(({ route, distributorId, distributorName }) => {
            const cities = route.cities ?? [];
            const places = cities.slice(0, 2).join(' · ');
            const location = [places, (route.states ?? []).join(', ')].filter(Boolean).join(' · ');
            return (
              <a
                className="route-card"
                href={routePath(distributorId, route.routeNumber)}
                key={`${distributorId}:${route.routeNumber}`}
                aria-label={`Open route ${route.routeNumber} for ${distributorName}, ${plural(route.stopCount, 'customer')}`}
              >
                <div className="route-card-heading">
                  <div>
                    <span className="route-card-eyebrow">{distributorName}</span>
                    <h3>Route {route.routeNumber}</h3>
                  </div>
                  <span className="route-card-arrow" aria-hidden="true">
                    ↗
                  </span>
                </div>
                <RouteMap
                  points={route.mapPoints ?? []}
                  total={route.stopCount}
                  label={`Route ${route.routeNumber} for ${distributorName}`}
                />
                <div className="route-card-details">
                  <strong>{plural(route.stopCount, 'customer')}</strong>
                  <span>{location || 'Location details unavailable'}</span>
                </div>
              </a>
            );
          })}
        </div>
      ) : (
        <div className="panel empty">
          {selectedIds.length === 0
            ? 'Select a distributor to show routes.'
            : query
              ? 'No routes match your search.'
              : 'No routes are available for the selected distributors.'}
        </div>
      )}
    </section>
  );
}
